import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const EMPTY = {
  name: '',
  description: '',
  onec: null,    // выбранная позиция кэша 1С: { id, code, name } | null
  zupCalcTypeCode: '',
  archive: false,
};

export default function ExtraSubjectEditModal({ mode, subject, onClose, onSaved }) {
  const [data, setData]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (mode === 'edit' && subject) {
      setData({
        name:        subject.name || '',
        description: subject.description || '',
        onec:        subject.onec ? {
          id:   subject.onec.id,
          code: subject.onec.code,
          name: subject.onec.name,
        } : null,
        zupCalcTypeCode: subject.onecZup?.calcTypeCode || '',
        archive:     !!subject.archive,
      });
    } else {
      setData(EMPTY);
    }
  }, [mode, subject?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        name:               data.name.trim(),
        description:        data.description.trim(),
        onecExtraSubjectId: data.onec?.id || null,
        onecZupCalcTypeCode: data.zupCalcTypeCode.trim() || null,
        archive:            data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/extra-subjects', payload)
        : await api.put(`/api/extra-subjects/${subject.id}`, payload);
      onSaved?.(res.subject);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новый предмет (доп. занятия)' : 'Редактирование предмета'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Название *</label>
              <input
                className="sd-edit__input"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="например, «Программирование» или «Англ. (инд.)»"
                required
                autoFocus
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Номенклатура 1С</label>
              <OnecExtraSubjectPicker
                value={data.onec}
                onChange={(o) => set('onec', o)}
              />
              <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>
                Без привязки к 1С нельзя выгружать платежи по этому предмету.
                Если позиции нет в списке — её нужно сначала завести бухгалтеру в 1С,
                кэш обновится автоматически (раз в 15 минут).
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Описание</label>
              <textarea
                className="sd-edit__input"
                style={{ height: 'auto', minHeight: 60, padding: 8, resize: 'vertical' }}
                value={data.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Краткое описание (необязательно)"
                rows={2}
              />
            </div>

            {/* === 1С ЗУП — для зарплат учителям, помесячно ===
                Схема «Разовые начисления»: вид начисления «Доп. <предмет>»
                создаётся в ЗУП автоматически копией эталона. */}
            <div className="sd-edit__field sd-edit__field--full" style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 6 }}>
              <label className="sd-edit__label" style={{ fontSize: 13 }}>
                1С ЗУП (для зарплат учителям)
              </label>
              <div className="sd-edit__hint" style={{ marginBottom: 8 }}>
                Выгрузка создаёт в ЗУП документ «Разовое начисление» по предмету; вид
                начисления «Доп. {'{предмет}'}» находится или создаётся автоматически.
                Обычно здесь ничего заполнять не нужно.
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Вид начисления (опционально)</label>
              <ZupCalcTypePicker
                value={data.zupCalcTypeCode}
                onChange={(code) => set('zupCalcTypeCode', code || '')}
              />
              <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>
                Выбирайте, только если предмет должен идти через СВОЙ существующий вид
                начисления ЗУП. Пусто — вид «Доп. {'{предмет}'}» создастся автоматически.
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="ref-checkbox">
                <input
                  type="checkbox"
                  checked={data.archive}
                  onChange={(e) => set('archive', e.target.checked)}
                />
                <span>В архиве (скрыт из выпадашек выбора)</span>
              </label>
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Пикер вида начисления из кэша 1С ЗУП (onec_zup_calc_types).             */
/* value — Код вида расчёта (его выгрузка шлёт в calc_type_code).          */
/* Список маленький (десятки строк), поэтому грузим целиком и фильтруем    */
/* на клиенте. «Обновить из 1С» дёргает pull и перечитывает кэш.           */
/* ----------------------------------------------------------------------- */
function ZupCalcTypePicker({ value, onChange }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [rows, setRows]       = useState(null);   // null = ещё не грузили
  const [syncing, setSyncing] = useState(false);
  const rootRef = useRef(null);

  const load = () => api.get('/api/onec/zup-calc-types?limit=500')
    .then((res) => setRows(res.rows || []))
    .catch(() => setRows([]));

  // Грузим кэш сразу при монтировании: имя выбранного вида нужно
  // показывать и при закрытом списке.
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const refreshFromOnec = async () => {
    setSyncing(true);
    try {
      await api.post('/api/onec/zup-calc-types/sync', {});
      // Pull фоновый и быстрый (один GET к ЗУП) — подождём и перечитаем.
      await new Promise((r) => setTimeout(r, 1500));
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось запустить обновление');
    } finally {
      setSyncing(false);
    }
  };

  const current = (rows || []).find((r) => r.code === value) || null;
  const q = search.trim().toLowerCase();
  const filtered = (rows || []).filter((r) =>
    !q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q));

  return (
    <div className="onec-picker" ref={rootRef}>
      <div className="onec-picker__row">
        <button type="button" className="onec-picker__current" onClick={() => setOpen((v) => !v)}>
          <span className="onec-picker__value">
            {value ? (
              current ? (
                <>
                  <span className="onec-picker__code">{current.code}</span>
                  <span className="onec-picker__name" title={current.name}>{current.name}</span>
                </>
              ) : (
                <>
                  <span className="onec-picker__code" title="Код сохранён, но в кэше видов начислений его нет — обновите список из 1С">{value}</span>
                  <span className="onec-picker__name" style={{ color: 'var(--fg-faint)' }}>нет в кэше</span>
                </>
              )
            ) : (
              <span className="onec-picker__empty">— автосоздание «Доп. {'{предмет}'}» —</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>
            <Icon name="chevronDown" size={12} />
          </span>
        </button>
        {value && (
          <button type="button" className="onec-picker__clear"
                  onClick={() => { onChange(null); setSearch(''); }} title="Очистить (автосоздание)">
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="onec-picker__menu">
          <input
            className="onec-picker__search"
            placeholder="Поиск по коду или названию…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="onec-picker__list">
            {rows === null && <div className="onec-picker__hint">Загрузка…</div>}
            {rows !== null && filtered.length === 0 && (
              <div className="onec-picker__hint">
                {q ? 'Ничего не найдено' : 'Кэш пуст — нажмите «Обновить из 1С» ниже.'}
              </div>
            )}
            {filtered.map((r) => (
              <button
                key={r.code}
                type="button"
                className="onec-picker__item"
                title={r.name}
                onClick={() => { onChange(r.code); setOpen(false); setSearch(''); }}
              >
                <span className="onec-picker__code">{r.code}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                <span />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="onec-picker__hint"
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              borderTop: '1px solid var(--border)', color: 'var(--green-700)', cursor: 'pointer',
            }}
            onClick={refreshFromOnec}
            disabled={syncing}
          >
            {syncing ? 'Обновляем из 1С ЗУП…' : '↻ Обновить из 1С'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Пикер номенклатуры 1С: тот же стиль, что у onec-picker для контрагентов */
/* ----------------------------------------------------------------------- */
function OnecExtraSubjectPicker({ value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad(true);
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ limit: '100' });
      if (search.trim()) qs.set('search', search.trim());
      api.get('/api/onec/extra-subjects?' + qs.toString())
        .then((res) => { if (!cancelled) setRows(res.rows || []); })
        .catch(() => { if (!cancelled) setRows([]); })
        .finally(() => { if (!cancelled) setLoad(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search]);

  return (
    <div className="onec-picker" ref={rootRef}>
      <div className="onec-picker__row">
        <button
          type="button"
          className="onec-picker__current"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="onec-picker__value">
            {value ? (
              <>
                <span className="onec-picker__code">{value.code}</span>
                <span className="onec-picker__name">{value.name}</span>
              </>
            ) : (
              <span className="onec-picker__empty">— не привязан —</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>
            <Icon name="chevronDown" size={12} />
          </span>
        </button>
        {value && (
          <button
            type="button"
            className="onec-picker__clear"
            onClick={() => { onChange(null); setSearch(''); }}
            title="Отвязать"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="onec-picker__menu">
          <input
            className="onec-picker__search"
            placeholder="Поиск по коду или названию…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="onec-picker__list">
            {loading && rows.length === 0 && (
              <div className="onec-picker__hint">Загрузка…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="onec-picker__hint">
                {search ? 'Ничего не найдено' : 'Кэш пуст. Дождитесь синхронизации с 1С.'}
              </div>
            )}
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                className="onec-picker__item"
                onClick={() => { onChange({ id: r.id, code: r.code, name: r.name }); setOpen(false); setSearch(''); }}
              >
                <span className="onec-picker__code">{r.code}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                <span />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
